import React, { createContext, useContext, useState, useEffect } from 'react';
import { subscriptionAPI } from '../utils/subscriptionApi';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  return context || {};
};

export const SubscriptionProvider = ({ children }) => {
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [featureUsage, setFeatureUsage] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSubscriptionData();
  }, []);

  const loadSubscriptionData = async () => {
    try {
      setLoading(true);
      const accountId = localStorage.getItem('tenantId');
      const [subscriptionData, plansData, usageData] = await Promise.all([
        subscriptionAPI.getCurrentSubscription().catch(() => null),
        subscriptionAPI.getPlans().catch(() => []),
        subscriptionAPI.getFeatureUsage(accountId).catch(() => ({})),
      ]);

      setCurrentSubscription(subscriptionData);
      setPlans(plansData);
      console.log('Loaded plans from API:', plansData);
      setFeatureUsage(usageData);
    } catch (error) {
      console.error('Error loading subscription data:', error);
      // Set empty state to prevent infinite loading
      setPlans([]);
      setFeatureUsage({});
    } finally {
      setLoading(false);
    }
  };

  const subscribeToPlan = async (planId) => {
    try {
      const newSubscription = await subscriptionAPI.createSubscription(planId);
      setCurrentSubscription(newSubscription);
      return newSubscription;
    } catch (error) {
      throw error;
    }
  };

  const changePlan = async (planId) => {
    try {
      const updatedSubscription = await subscriptionAPI.changePlan(planId);
      setCurrentSubscription(updatedSubscription);
      return updatedSubscription;
    } catch (error) {
      throw error;
    }
  };

  const cancelSubscription = async (reason) => {
    try {
      const cancelledSubscription = await subscriptionAPI.cancelSubscription(reason);
      setCurrentSubscription(cancelledSubscription);
      return cancelledSubscription;
    } catch (error) {
      throw error;
    }
  };

  const checkFeatureAccess = async (feature) => {
    try {
      const { hasAccess } = await subscriptionAPI.checkFeatureAccess(feature);
      return hasAccess;
    } catch (error) {
      console.error('Error checking feature access:', error);
      return false;
    }
  };

  const getCurrentPlan = () => {
    if (!currentSubscription) return null;
    // Prefer the plan details embedded in the subscription object if available
    if (currentSubscription.plan && typeof currentSubscription.plan === 'object' && currentSubscription.plan.name) {
      return currentSubscription.plan;
    }
    // Fallback to finding it in the plans list
    const planId = currentSubscription.plan_id || currentSubscription.planId || (currentSubscription.plan && currentSubscription.plan.id);
    if (planId && Array.isArray(plans) && plans.length > 0) {
      const found = plans.find(plan => Number(plan.id) === Number(planId));
      if (found) return found;
    }
    return currentSubscription.plan || null;
  };

  const currentPlan = getCurrentPlan();
  const isPaidUser = Boolean(
    currentSubscription &&
    (currentSubscription.status === 'active' || currentSubscription.is_lifetime) &&
    (
      currentSubscription.is_lifetime ||
      (currentPlan && (
        Number(currentPlan.price) > 0 ||
        currentPlan.name === '6 Months' ||
        currentPlan.name === '1 Month' ||
        currentPlan.name === '1 Year' ||
        currentPlan.name === '10 Years' ||
        currentPlan.name?.toLowerCase().includes('month') ||
        currentPlan.name?.toLowerCase().includes('year')
      )) ||
      (currentSubscription.plan_id && Number(currentSubscription.plan_id) > 2) ||
      (currentSubscription.planId && Number(currentSubscription.planId) > 2)
    )
  );

  const isFeatureAvailable = (feature) => {
    const plan = getCurrentPlan();
    if (!plan) return false;
    return plan.features && plan.features[feature];
  };

  const getFeatureLimit = (feature) => {
    const plan = getCurrentPlan();
    if (!plan) return 0;

    switch (feature) {
      case 'invoices':
        return plan.max_bills_per_month;
      case 'customers':
        return plan.max_customers;
      case 'transactions':
        return plan.max_transactions_per_month;
      default:
        return 0;
    }
  };

  const getFeatureUsage = (feature) => {
    return featureUsage[feature] || 0;
  };

  const isUsageLimitReached = (feature) => {
    const limit = getFeatureLimit(feature);
    const usage = getFeatureUsage(feature);
    return limit > 0 && usage >= limit;
  };

  const value = {
    currentSubscription,
    plans,
    featureUsage,
    loading,
    isPaidUser,
    subscribeToPlan,
    changePlan,
    cancelSubscription,
    checkFeatureAccess,
    getCurrentPlan,
    isFeatureAvailable,
    getFeatureLimit,
    getFeatureUsage,
    isUsageLimitReached,
    refreshData: loadSubscriptionData,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}; 